/**
 * Flowlytics Pixel SDK - First-Party Data Collector
 *
 * Collects first-party data that survives third-party cookie deprecation:
 * - Click IDs from URL params (gclid, fbclid, ttclid, li_fat_id, msclkid)
 * - Form submissions (email, phone, name from forms on the page)
 * - Local storage persistence for cross-session identity
 *
 * Created: 2026-02-08 (Phase 1, Week 4 — Pixel SDK Hardening)
 */

const FP_STORAGE_KEY = "flw_fp_data";

// Click ID parameters to extract from URL
const CLICK_ID_PARAMS = {
  gclid: "google",
  gbraid: "google",
  wbraid: "google",
  fbclid: "facebook",
  ttclid: "tiktok",
  li_fat_id: "linkedin",
  msclkid: "microsoft",
  twclid: "twitter",
  epik: "pinterest",
  ScCid: "snapchat",
  rdt_cid: "reddit",
};

// UTM parameters
const UTM_PARAMS = [
  "utm_source",
  "utm_medium",
  "utm_campaign",
  "utm_term",
  "utm_content",
];

class FirstPartyCollector {
  constructor(config) {
    this.config = config;
    this._storedData = null;
  }

  /**
   * Collect all first-party data.
   * Returns structured object with click IDs, UTMs, and stored identity.
   */
  collect() {
    const clickIds = this._extractClickIds();
    const utmParams = this._extractUTMParams();
    const storedIdentity = this._getStoredIdentity();

    // Hold in memory — persist to localStorage only after marketing consent (ePrivacy)
    if (Object.keys(clickIds).length > 0 || Object.keys(utmParams).length > 0) {
      this._pendingClickIds = clickIds;
      this._pendingUtmParams = utmParams;
    }

    return {
      click_ids: {
        ...storedIdentity.clickIds,
        ...clickIds, // Current URL overrides stored
      },
      utm: {
        ...storedIdentity.utmParams,
        ...utmParams,
      },
      stored_identity: storedIdentity.identity || null,
      first_visit: storedIdentity.firstVisit || new Date().toISOString(),
      visit_count: (storedIdentity.visitCount || 0) + 1,
    };
  }

  /**
   * Capture form submission data (email, phone, name).
   * Call this on form submit events to enrich identity.
   */
  /**
   * A checkbox that speaks about consent, as {name, checked, preselected,
   * label} — or null for any other checkbox.
   *
   * Only consent checkboxes leave the page, recognised by name or visible text:
   * on a clinic's site an ordinary checkbox ("interested in: implants") can
   * reveal health data. The backend records a marketing consent from the one
   * checkbox the site owner declared, and never from a pre-ticked one
   * (`preselected` is the HTML default, CJEU C-673/17 Planet49) — see
   * funnel_automation_backend/services/marketing-email-consent.js.
   */
  _consentCheckbox(input) {
    const name = input.name || input.id || "";
    if (!name) return null;

    let label = "";
    try {
      const labelEl =
        (input.labels && input.labels[0]) ||
        (input.closest ? input.closest("label") : null);
      label =
        (labelEl && labelEl.textContent) ||
        input.getAttribute("aria-label") ||
        "";
    } catch (_) {
      label = "";
    }
    label = label.replace(/\s+/g, " ").trim().slice(0, 300);

    const vocabulary =
      /consen|privacy|newsletter|marketing|promozion|commercial|comunicazion|informativa|gdpr|acconsent|autorizz|iscriv|subscribe|opt.?in|acceptance/i;
    if (!vocabulary.test(name) && !vocabulary.test(label)) return null;

    return {
      name: name.slice(0, 128),
      checked: input.checked === true,
      preselected: input.defaultChecked === true,
      label: label || null,
    };
  }

  captureFormData(formElement) {
    if (!formElement) return null;

    const data = {};
    const inputs = formElement.querySelectorAll("input, select, textarea");

    for (const input of inputs) {
      const name = (input.name || input.id || "").toLowerCase();
      const value = input.value;

      // Checkboxes first: their value ("1", "on") is shorter than the length
      // filter below, and what matters is whether they are ticked.
      if (input.type === "checkbox") {
        const box = this._consentCheckbox(input);
        if (box) {
          data.consent_checkboxes = data.consent_checkboxes || [];
          if (data.consent_checkboxes.length < 10) {
            data.consent_checkboxes.push(box);
          }
        }
        continue;
      }

      if (!value || value.length < 2) continue;

      // Email detection
      if (name.includes("email") || input.type === "email") {
        data.email = value.toLowerCase().trim();
      }
      // Phone detection
      else if (
        name.includes("phone") ||
        name.includes("tel") ||
        input.type === "tel"
      ) {
        data.phone = value.trim();
      }
      // Name detection
      else if (
        name.includes("name") &&
        !name.includes("user") &&
        !name.includes("company")
      ) {
        if (name.includes("first") || name === "fname") {
          data.first_name = value.trim();
        } else if (name.includes("last") || name === "lname") {
          data.last_name = value.trim();
        } else {
          data.full_name = value.trim();
        }
      }
      // Company / Organization (B2B profiling)
      else if (
        name.includes("company") ||
        name.includes("organization") ||
        name.includes("azienda") ||
        name.includes("societa")
      ) {
        data.company = value.trim();
      }
      // Job title / Role
      else if (
        name.includes("job") ||
        name.includes("title") ||
        name.includes("role") ||
        name.includes("ruolo") ||
        name.includes("posizione")
      ) {
        data.job_title = value.trim();
      }
      // Industry / Sector
      else if (
        name.includes("industry") ||
        name.includes("sector") ||
        name.includes("settore")
      ) {
        data.industry = value.trim();
      }
      // Company size
      else if (
        name.includes("size") ||
        name.includes("employees") ||
        name.includes("dipendenti")
      ) {
        data.company_size = value.trim();
      }
      // VAT / P.IVA (B2B identifier)
      else if (
        name.includes("vat") ||
        name.includes("piva") ||
        name.includes("p_iva") ||
        name.includes("partita_iva") ||
        name.includes("tax_id")
      ) {
        data.vat_id = value.trim();
      }
      // City
      else if (
        name.includes("city") ||
        name.includes("citta") ||
        name === "city"
      ) {
        data.city = value.trim();
      }
      // Website
      else if (
        name.includes("website") ||
        name.includes("sito") ||
        name.includes("url")
      ) {
        data.website = value.trim();
      }
    }

    // Store captured identity for cross-session use
    if (data.email || data.phone) {
      this._persistIdentity(data);
    }

    return Object.keys(data).length > 0 ? data : null;
  }

  /**
   * Collect behavioral profile data for audience optimization.
   * Captures: visit patterns, content interests, engagement signals.
   * Called on every page_view to build up profile over time.
   */
  collectBehaviorProfile() {
    const now = new Date();
    const stored = this._getStoredIdentity();
    const profile = stored.behaviorProfile || {};

    // Visit timing patterns (hour of day, day of week)
    const hour = now.getHours();
    const dayOfWeek = now.toLocaleDateString("en-US", { weekday: "long" });
    profile.visit_hours = profile.visit_hours || {};
    profile.visit_hours[hour] = (profile.visit_hours[hour] || 0) + 1;
    profile.visit_days = profile.visit_days || {};
    profile.visit_days[dayOfWeek] = (profile.visit_days[dayOfWeek] || 0) + 1;

    // Page categories (from URL path)
    const path = window.location.pathname.toLowerCase();
    profile.pages_visited = profile.pages_visited || [];
    if (
      !profile.pages_visited.includes(path) &&
      profile.pages_visited.length < 50
    ) {
      profile.pages_visited.push(path);
    }

    // Content interests (from page path keywords)
    const interestKeywords = {
      pricing: ["pricing", "prezzi", "piani", "plans", "price"],
      features: ["features", "funzionalita", "caratteristiche"],
      demo: ["demo", "trial", "prova", "test"],
      docs: ["docs", "documentation", "guide", "help", "faq"],
      about: ["about", "chi-siamo", "team", "company"],
      blog: ["blog", "news", "articoli", "risorse"],
      contact: ["contact", "contatti", "support"],
      checkout: ["checkout", "cart", "carrello", "payment", "pagamento"],
      signup: ["signup", "register", "registra", "iscriviti"],
      login: ["login", "accedi", "signin"],
    };

    profile.content_interests = profile.content_interests || {};
    for (const [category, keywords] of Object.entries(interestKeywords)) {
      if (keywords.some((kw) => path.includes(kw))) {
        profile.content_interests[category] =
          (profile.content_interests[category] || 0) + 1;
      }
    }

    // Referrer tracking (where users come from)
    const referrer = document.referrer;
    if (referrer && !referrer.includes(window.location.hostname)) {
      profile.referrers = profile.referrers || [];
      try {
        const refHost = new URL(referrer).hostname;
        if (
          !profile.referrers.includes(refHost) &&
          profile.referrers.length < 20
        ) {
          profile.referrers.push(refHost);
        }
      } catch (_) {}
    }

    // Session depth (unique pages per session)
    profile.total_unique_pages = (profile.pages_visited || []).length;

    // Peak engagement time
    const peakHour = Object.entries(profile.visit_hours || {}).sort(
      (a, b) => b[1] - a[1],
    )[0];
    if (peakHour) profile.peak_hour = parseInt(peakHour[0]);

    // User intent signals
    profile.visited_pricing = (profile.content_interests?.pricing || 0) > 0;
    profile.visited_signup = (profile.content_interests?.signup || 0) > 0;
    profile.visited_demo = (profile.content_interests?.demo || 0) > 0;
    profile.engagement_level = this._calculateEngagementLevel(profile);

    // Persist updated profile
    const updated = { ...stored, behaviorProfile: profile };
    try {
      localStorage.setItem(FP_STORAGE_KEY, JSON.stringify(updated));
    } catch (_) {}

    return profile;
  }

  /**
   * Calculate engagement level from behavior profile
   */
  _calculateEngagementLevel(profile) {
    let score = 0;
    const pages = (profile.pages_visited || []).length;
    if (pages >= 5) score += 3;
    else if (pages >= 3) score += 2;
    else if (pages >= 1) score += 1;

    if (profile.visited_pricing) score += 3;
    if (profile.visited_signup) score += 2;
    if (profile.visited_demo) score += 2;
    if ((profile.content_interests?.features || 0) > 0) score += 1;

    if (score >= 8) return "high_intent";
    if (score >= 5) return "engaged";
    if (score >= 2) return "interested";
    return "browsing";
  }

  /**
   * Setup automatic form capture listeners.
   * Attaches to all forms on the page, captures data on submit.
   */
  setupFormCapture(callback) {
    if (typeof document === "undefined") return;

    document.addEventListener(
      "submit",
      (e) => {
        const form = e.target;
        if (form.tagName !== "FORM") return;

        const formData = this.captureFormData(form);
        if (formData && callback) {
          callback(formData);
        }
      },
      true,
    );

    if (this.config.debug) {
      console.log("[Flowlytics FirstParty] Form capture listeners attached");
    }
  }

  /**
   * Get stored click IDs (from previous sessions via localStorage).
   */
  getStoredClickIds() {
    const stored = this._getStoredIdentity();
    return stored.clickIds || {};
  }

  /**
   * Extract click IDs from current URL parameters.
   */
  _extractClickIds() {
    const clickIds = {};

    try {
      const params = new URLSearchParams(window.location.search);

      for (const [param, platform] of Object.entries(CLICK_ID_PARAMS)) {
        const value = params.get(param);
        if (value) {
          clickIds[param] = {
            value,
            platform,
            captured_at: new Date().toISOString(),
            page_url: window.location.href,
          };
        }
      }
    } catch (e) {
      // URLSearchParams may not be available
    }

    return clickIds;
  }

  /**
   * Extract UTM parameters from current URL.
   */
  _extractUTMParams() {
    const utms = {};

    try {
      const params = new URLSearchParams(window.location.search);

      for (const param of UTM_PARAMS) {
        const value = params.get(param);
        if (value) {
          utms[param] = value;
        }
      }

      if (Object.keys(utms).length > 0) {
        utms._captured_at = new Date().toISOString();
        utms._landing_page = window.location.href;
      }
    } catch (e) {
      // URLSearchParams may not be available
    }

    return utms;
  }

  /**
   * Get stored identity data from localStorage.
   */
  _getStoredIdentity() {
    if (this._storedData) return this._storedData;

    try {
      const raw = localStorage.getItem(FP_STORAGE_KEY);
      if (raw) {
        this._storedData = JSON.parse(raw);
        return this._storedData;
      }
    } catch (e) {
      // localStorage unavailable
    }

    return {};
  }

  /**
   * Flush pending click IDs and UTM data to localStorage after marketing consent.
   * Called from _flushPreConsentQueue when consent is decided.
   */
  persistIfConsented(marketingGranted) {
    if (marketingGranted && (this._pendingClickIds || this._pendingUtmParams)) {
      this._persistData({
        clickIds: this._pendingClickIds || {},
        utmParams: this._pendingUtmParams || {},
      });
      this._pendingClickIds = null;
      this._pendingUtmParams = null;
    }
  }

  /**
   * Persist click IDs and UTM data to localStorage.
   */
  _persistData({ clickIds, utmParams }) {
    try {
      const existing = this._getStoredIdentity();

      const updated = {
        ...existing,
        clickIds: { ...(existing.clickIds || {}), ...clickIds },
        utmParams: { ...(existing.utmParams || {}), ...utmParams },
        firstVisit: existing.firstVisit || new Date().toISOString(),
        visitCount: (existing.visitCount || 0) + 1,
        lastVisit: new Date().toISOString(),
      };

      localStorage.setItem(FP_STORAGE_KEY, JSON.stringify(updated));
      this._storedData = updated;
    } catch (e) {
      // localStorage unavailable
    }
  }

  /**
   * Persist captured form identity (email, phone) to localStorage.
   */
  _persistIdentity(data) {
    try {
      const existing = this._getStoredIdentity();

      const updated = {
        ...existing,
        identity: {
          ...(existing.identity || {}),
          ...data,
          captured_at: new Date().toISOString(),
        },
      };

      localStorage.setItem(FP_STORAGE_KEY, JSON.stringify(updated));
      this._storedData = updated;
    } catch (e) {
      // localStorage unavailable
    }
  }
}

export default FirstPartyCollector;
